import { type ComponentType, type ReactElement, useCallback, useRef } from "react";

import {
    FixedSizeList,
    type FixedSizeListProps,
    type ListChildComponentProps,
    type ListOnItemsRenderedProps,
} from "react-window";

import { type VirtualizedListData } from "../models";

interface VirtualizedListProps<TListItem, TData> extends Omit<FixedSizeListProps, "itemCount" | "itemData"> {
    data: VirtualizedListData<TListItem, TData>;
    children: ComponentType<ListChildComponentProps>;
    hasMore: boolean;
}

export const VirtualizedList = <TListItem, TData>({
    data,
    hasMore,
    height,
    width,
    itemSize,
    children,
}: VirtualizedListProps<TListItem, TData>): ReactElement => {
    const { list } = data;
    const stopIndexRef = useRef(0);
    const itemsCount = hasMore ? list.length + 1 : list.length;

    const onItemsRendered = useCallback(
        (props: ListOnItemsRenderedProps) => {
            const { visibleStopIndex } = props;
            if (visibleStopIndex === list.length && stopIndexRef.current !== visibleStopIndex) {
                stopIndexRef.current = visibleStopIndex;
            }
        },
        [list, stopIndexRef],
    );

    return (
        <FixedSizeList
            height={height}
            itemCount={itemsCount}
            itemData={data}
            itemSize={itemSize}
            width={width}
            onItemsRendered={onItemsRendered}
        >
            {children}
        </FixedSizeList>
    );
};
